import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../ui/Toast'
import { useConfirm } from '../ui/ConfirmDialog'
import Modal from '../ui/Modal'
import Button from '../ui/Button'
import { MoneyInput } from '../ui/Input'
import { fmt, dateStr, uid } from '../../utils/formatters'
import { exportToExcel } from '../../utils/exportExcel'
import { SkeletonStats, SkeletonList } from '../ui/Skeleton'

export default function Materials() {
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const [materials, setMaterials] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [show, setShow] = useState(false)
  const [edit, setEdit] = useState(null)
  const [filterProject, setFilterProject] = useState('all')
  const [filterStock, setFilterStock] = useState('all')
  const [search, setSearch] = useState('')

  const blank = { project_id: '', name: '', unit: '', stock: 0, min_stock: 0, last_delivery: new Date().toISOString().split('T')[0], price: 0 }
  const [form, setForm] = useState(blank)

  useEffect(() => { if (!user) return; fetchData() }, [user])

  const fetchData = async () => {
    setLoading(true)
    const [{ data: mat }, { data: proj }] = await Promise.all([
      supabase.from('materials').select('*').eq('user_id', user.id).order('name'),
      supabase.from('projects').select('*').eq('user_id', user.id),
    ])
    if (mat) setMaterials(mat)
    if (proj) setProjects(proj)
    setLoading(false)
  }

  const save = async () => {
    if (!form.name) { toast.show('Nama material wajib diisi', 'error'); return }
    const data = { ...form, stock: parseFloat(form.stock) || 0, min_stock: parseFloat(form.min_stock) || 0, price: parseInt(form.price) || 0, user_id: user.id }
    if (edit) {
      await supabase.from('materials').update(data).eq('id', edit)
    } else {
      await supabase.from('materials').insert([{ ...data, id: uid() }])
    }
    setShow(false)
    toast.show(edit ? 'Material diperbarui' : 'Material ditambahkan')
    fetchData()
  }

  const del = async (id, name) => {
    const ok = await confirm(`Hapus material "${name}"?`, { title: 'Hapus Material', icon: '📦', confirmLabel: 'Hapus', confirmVariant: 'danger' })
    if (!ok) return
    await supabase.from('materials').delete().eq('id', id)
    toast.show('Material dihapus')
    fetchData()
  }

  const lowStockCount = materials.filter(m => m.stock <= m.min_stock).length

  const filtered = materials.filter(m => {
    const matchP = filterProject === 'all' || m.project_id === filterProject
    const matchSt = filterStock === 'all' || (filterStock === 'low' && m.stock <= m.min_stock) || (filterStock === 'ok' && m.stock > m.min_stock)
    const matchS = !search || m.name.toLowerCase().includes(search.toLowerCase())
    return matchP && matchSt && matchS
  })

  if (loading) return <div><SkeletonStats cols={4} /><SkeletonList count={4} /></div>

  return (
    <div>
      <div className="stats-grid" style={{ marginBottom: 14 }}>
        <div className="stat-card"><div className="stat-value">{materials.length}</div><div className="stat-label">Total Material</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: lowStockCount ? 'var(--danger)' : 'var(--success)' }}>{lowStockCount}</div><div className="stat-label">Stok Menipis</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--success)' }}>{materials.length - lowStockCount}</div><div className="stat-label">Stok Aman</div></div>
        <div className="stat-card"><div className="stat-value" style={{ color: 'var(--accent)', fontSize: 15 }}>{fmt(materials.reduce((s, m) => s + (m.stock * m.price), 0))}</div><div className="stat-label">Nilai Inventory</div></div>
      </div>

      {lowStockCount > 0 && (
        <div style={{ background: 'var(--warning-bg)', border: '1px solid var(--warning)', borderRadius: 'var(--radius-lg)', padding: '8px 14px', marginBottom: 12, fontSize: 12, color: 'var(--warning)', fontWeight: 600 }}>
          ⚠️ {lowStockCount} material stok menipis — perlu restock!
        </div>
      )}

      <div className="filter-row">
        <div className="search-box">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
          <input placeholder="Cari material..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 160 }} value={filterProject} onChange={e => setFilterProject(e.target.value)}>
          <option value="all">Semua Proyek</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select className="form-select" style={{ width: 140 }} value={filterStock} onChange={e => setFilterStock(e.target.value)}>
          <option value="all">Semua Stok</option>
          <option value="low">⚠ Menipis</option>
          <option value="ok">✅ Aman</option>
        </select>
        <Button variant="secondary" onClick={() => { exportToExcel(filtered.map(m => ({ 'Nama': m.name, 'Unit': m.unit, 'Stok': m.stock, 'Min': m.min_stock, 'Harga': m.price })), 'Data-Material'); toast.show('Excel diunduh') }}>📊</Button>
        <Button onClick={() => { setForm(blank); setEdit(null); setShow(true) }}>➕ Material</Button>
      </div>

      {filtered.map(m => {
        const proj = projects.find(p => p.id === m.project_id)
        const low = m.stock <= m.min_stock
        const pct = m.min_stock > 0 ? Math.min(100, m.stock / m.min_stock * 100) : 100
        return (
          <div key={m.id} className="mobile-card-item" style={{ borderLeft: `3px solid ${low ? 'var(--danger)' : 'var(--success)'}` }}>
            <div className="mc-header">
              <div>
                <div className="mc-title">{m.name}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{proj?.name || 'Umum'}</div>
              </div>
              <span className={`badge ${low ? 'badge-danger' : 'badge-success'}`}>{low ? '⚠ Low' : '✓ OK'}</span>
            </div>
            <div style={{ marginBottom: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 10 }}>Stok: <b style={{ color: low ? 'var(--danger)' : 'var(--success)' }}>{m.stock} {m.unit}</b> / min {m.min_stock} {m.unit}</span>
                <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{Math.round(pct)}%</span>
              </div>
              <div className="progress-bar">
                <div className="fill" style={{ width: `${pct}%`, background: low ? 'var(--danger)' : 'var(--success)' }} />
              </div>
            </div>
            <div className="mc-row"><span className="mc-label">Harga Satuan</span><span className="mc-value" style={{ color: 'var(--accent)' }}>{fmt(m.price)}/{m.unit}</span></div>
            <div className="mc-row"><span className="mc-label">Last Delivery</span><span className="mc-value">{dateStr(m.last_delivery)}</span></div>
            <div className="mc-actions">
              <Button size="sm" variant="secondary" onClick={() => { setForm(m); setEdit(m.id); setShow(true) }}>✏️ Edit</Button>
              <Button size="sm" variant="danger" onClick={() => del(m.id, m.name)}>🗑️ Hapus</Button>
            </div>
          </div>
        )
      })}

      {!filtered.length && <div className="empty-state"><div className="empty-icon">📦</div><p>Belum ada material</p></div>}

      <Modal show={show} onClose={() => setShow(false)} title={edit ? '✏️ Edit Material' : '➕ Tambah Material'}
        footer={<><Button variant="secondary" onClick={() => setShow(false)}>Batal</Button><Button onClick={save}>💾 Simpan</Button></>}
      >
        <div className="form-group">
          <label className="form-label">Proyek</label>
          <select className="form-select" value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}>
            <option value="">Pilih Proyek</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Nama Material *</label>
          <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Cth: Semen PCC 50kg" />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Satuan</label>
            <input className="form-input" value={form.unit} onChange={e => setForm({ ...form, unit: e.target.value })} placeholder="sak, m³, kg, bh" />
          </div>
          <div className="form-group">
            <label className="form-label">Harga/Satuan</label>
            <MoneyInput value={form.price} onChange={v => setForm({ ...form, price: v })} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Stok Saat Ini</label>
            <input className="form-input" type="number" value={form.stock} onChange={e => setForm({ ...form, stock: e.target.value })} />
          </div>
          <div className="form-group">
            <label className="form-label">Min Stok (Alert)</label>
            <input className="form-input" type="number" value={form.min_stock} onChange={e => setForm({ ...form, min_stock: e.target.value })} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Last Delivery</label>
          <input className="form-input" type="date" value={form.last_delivery} onChange={e => setForm({ ...form, last_delivery: e.target.value })} />
        </div>
      </Modal>
    </div>
  )
}